import {
  CoverageCategory,
  type CoverageListItem,
} from '@/services/types/coverage-types';

import { CoverageEmptyState } from './CoverageEmptyState';
import { CoverageList } from './CoverageList';
import { CoverageListSkeleton } from './CoverageListSkeleton';
import { CoverageSelectionManager } from './merge-coverage-entries/CoverageSelectionManager';
import { CoverageContentWrapper } from './shared/CoverageContentWrapper';
import { CoverageErrorState } from './shared/CoverageErrorState';

interface CoverageContentProps {
  isLoading: boolean;
  error: boolean;
  coverages: CoverageListItem[] | undefined;
  selectionMode: boolean;
  matterId: string;
  filterCategory?: CoverageCategory;
  onFilterChange: (category: CoverageCategory | undefined) => void;
  onCoverageSelect: (coverageId: string) => void;
  onCreateCoverage: () => void;
  onUpdate: () => void;
  onRefetch: () => void;
  coverageCreationLocked?: boolean;
}

export const CoverageContent = ({
  isLoading,
  error,
  coverages,
  selectionMode,
  matterId,
  filterCategory,
  onFilterChange,
  onCoverageSelect,
  onCreateCoverage,
  onUpdate,
  onRefetch,
  coverageCreationLocked = false,
}: CoverageContentProps) => {
  // Loading state
  if (isLoading) {
    return (
      <CoverageContentWrapper>
        <CoverageListSkeleton />
      </CoverageContentWrapper>
    );
  }

  // Error state
  if (error) {
    return (
      <CoverageContentWrapper>
        <CoverageErrorState onRetry={onRefetch} />
      </CoverageContentWrapper>
    );
  }

  // Empty state
  if (!coverages || coverages.length === 0) {
    return (
      <CoverageContentWrapper>
        <CoverageEmptyState
          onCreateCoverage={onCreateCoverage}
          locked={coverageCreationLocked}
        />
      </CoverageContentWrapper>
    );
  }

  // Selection mode content
  if (selectionMode) {
    return (
      <CoverageContentWrapper>
        <CoverageSelectionManager
          coverages={coverages}
          matterId={matterId}
          onCoverageView={onCoverageSelect}
          onUpdate={onUpdate}
          filterCategory={filterCategory}
          onFilterChange={onFilterChange}
        />
      </CoverageContentWrapper>
    );
  }

  // Normal list view
  return (
    <CoverageContentWrapper>
      <CoverageList
        coverages={coverages}
        onCoverageSelect={onCoverageSelect}
        filterCategory={filterCategory}
        matterId={matterId}
      />
    </CoverageContentWrapper>
  );
};
